import { LoadingBlockFullPage } from '@/components/loading/loading-block-full-page';
import { useAcl, useAppConfig } from '@/features/acl/acl-provider';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import type { AnalyticsEventPayload } from '@/features/analytics/types/analytics-events';
import { useExplorerView } from '@/features/explorer/hooks/use-explorer-query-params';
import { useAppState } from '@/lib/hooks/use-app-state';
import { cn } from '@/lib/utils';
import type { ExplorerSort, ExplorerViewFilters, ExplorerViewHandles } from '@/server/utils/explorer-utils';
import {
    ExplorerTopLevelEntity,
    allExplorerTabs,
    getExplorerFilterKeyByTabName,
    shouldHideUnselectedRows,
} from '@/server/utils/explorer-utils';
import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import { ExplorerFilters } from './explorer-filters';
import { ExplorerTopLevelEntityTabs } from './explorer-top-level-entity-tabs';
import { ExplorerViewAppliedFilters } from './explorer-view-applied-filters';
import { ExplorerViewClerk } from './explorer-view-clerk';
import { ExplorerViewCustomers } from './explorer-view-customers';
import { ExplorerViewIndustry } from './explorer-view-industry';
import { ExplorerViewNormalisedCategory } from './explorer-view-normalised-category';
import { ExplorerViewProduct } from './explorer-view-product';
import { ExplorerViewProductGroup } from './explorer-view-product-group';
import { ExplorerViewSalesReps } from './explorer-view-sales-reps';
import { ExplorerViewStorageLocations } from './explorer-view-storage-locations';
import { ExplorerViewSupplier } from './explorer-view-supplier';

interface Props {
    pageSource: AnalyticsEventPayload<'sales_report_viewed'>['page_source'];
    className?: string;
}

export const ExplorerView = forwardRef<ExplorerViewHandles, Props>(({ pageSource, className = '' }, ref) => {
    const { state, setState, defaultFilters: overrideFilters } = useExplorerView();

    const acl = useAcl();
    const { platoUser } = useAppState();

    const customerRef = useRef<ExplorerViewHandles>(null);
    const clerkRef = useRef<ExplorerViewHandles>(null);
    const supplierRef = useRef<ExplorerViewHandles>(null);
    const salesRepRef = useRef<ExplorerViewHandles>(null);
    const industryRef = useRef<ExplorerViewHandles>(null);
    const productRef = useRef<ExplorerViewHandles>(null);
    const productGroupRef = useRef<ExplorerViewHandles>(null);
    const storageLocationRef = useRef<ExplorerViewHandles>(null);
    const normalisedCategoryRef = useRef<ExplorerViewHandles>(null);

    const appConfig = useAppConfig();

    const sendEvent = useAnalytics();

    useEffect(() => {
        sendEvent('sales_report_viewed', {
            page_source: pageSource,
        });
    }, [pageSource]);

    const tabsToShow = useMemo(
        () =>
            allExplorerTabs.filter((tab: ExplorerTopLevelEntity) => {
                const filterKey = getExplorerFilterKeyByTabName(tab);

                if (
                    tab === ExplorerTopLevelEntity.NORMALISED_CATEGORY &&
                    appConfig.normalisedCategoryDepthsUsedInInsightsInExplorer === null
                ) {
                    // Hide normalised category tab when no category depths are found in insights
                    return false;
                }

                if (
                    tab === ExplorerTopLevelEntity.SALES_REP &&
                    overrideFilters &&
                    'customer' in overrideFilters
                ) {
                    // Because when customer is selected there is no use for sales rep tab
                    return false;
                }

                // We need to hide the product tab
                // apps/webapp/src/features/inventory/components/inventory-details.tsx
                if (
                    (tab === ExplorerTopLevelEntity.PRODUCT_GROUP ||
                        tab === ExplorerTopLevelEntity.PRODUCT) &&
                    overrideFilters &&
                    'product' in overrideFilters
                ) {
                    // Because when product is selected there might be only one product group
                    return false;
                }

                return acl.can('filter', 'SalesReportFilter', filterKey);
            }),
        [acl, overrideFilters],
    );

    // -----------------------------
    // Selected values
    // -----------------------------

    const {
        tab: selectedTab,
        searchString,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange,
        billing: selectedBillingStatus,
        sorting: selectedSortingFilter,
        targetUnit: selectedTargetUnit,
        yearPeriods: selectedYearPeriod,
        ...queryFilters
    } = state;

    const setAppliedFilters = (filters: ExplorerViewFilters) => {
        const usedFilters = (Object.keys(filters) as (keyof ExplorerViewFilters)[]).filter(
            (key) => filters[key]?.length > 0,
        );

        sendEvent('sales_report_filters_applied', {
            filters: usedFilters,
            number_of_filters: usedFilters.length,
        });

        setState((prevState) => ({
            ...prevState,
            ...filters,
        }));
    };

    const setSelectedTab = (tab: ExplorerTopLevelEntity) =>
        setState((prevState) => ({
            ...prevState,
            searchString: '',
            tab,
        }));

    const setSortingFilter = (sorting: ExplorerSort) => {
        setState((prevState) => ({
            ...prevState,
            sorting: sorting ?? undefined,
        }));
    };

    const combinedFilters = {
        ...queryFilters,
        ...overrideFilters,
    };

    if (!platoUser) {
        return <LoadingBlockFullPage className="mt-44" />;
    }

    const commonTabProps = {
        searchString,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange,
        filters: combinedFilters,
        billingStatus: selectedBillingStatus,
        targetUnit: selectedTargetUnit,
        sortingFilter: selectedSortingFilter,
        yearPeriods: selectedYearPeriod,
        setSortingFilter,
    } satisfies Omit<React.ComponentProps<typeof ExplorerViewStorageLocations>, 'onFiltersChange'>;

    return (
        <div className={cn('flex h-full w-full flex-col gap-3', className)}>
            <ExplorerFilters />

            <ExplorerTopLevelEntityTabs
                selectedTab={selectedTab}
                onTabChange={setSelectedTab}
                tabsToShow={tabsToShow}
            />

            <ExplorerViewAppliedFilters
                className="mb-0"
                tabsToShow={tabsToShow}
                selectedTab={selectedTab}
                filters={queryFilters}
                onFiltersChange={setAppliedFilters}
            />

            <div className={cn('flex flex-col grow-0 overflow-y-hidden')}>
                {selectedTab === ExplorerTopLevelEntity.STORAGE_LOCATION && (
                    <ExplorerViewStorageLocations
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                storageLocation: value,
                            })
                        }
                        key={selectedWindow}
                        ref={storageLocationRef}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.SALES_REP && (
                    <ExplorerViewSalesReps
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                salesRep: value,
                            })
                        }
                        key={selectedWindow}
                        ref={salesRepRef}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.CUSTOMER && (
                    <ExplorerViewCustomers
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                customer: value,
                            })
                        }
                        key={selectedWindow}
                        ref={customerRef}
                        hideUnselectedRows={shouldHideUnselectedRows({
                            tab: selectedTab,
                            defaultFilters: overrideFilters,
                        })}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.CLERK && (
                    <ExplorerViewClerk
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                clerk: value,
                            })
                        }
                        key={selectedWindow}
                        ref={clerkRef}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.INDUSTRY && (
                    <ExplorerViewIndustry
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                industry: value,
                            })
                        }
                        key={selectedWindow}
                        ref={industryRef}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.PRODUCT && (
                    <ExplorerViewProduct
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                product: value,
                            })
                        }
                        key={selectedWindow}
                        ref={productRef}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.PRODUCT_GROUP && (
                    <ExplorerViewProductGroup
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                productGroup: value,
                            })
                        }
                        key={selectedWindow}
                        ref={productGroupRef}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.NORMALISED_CATEGORY &&
                    appConfig.normalisedCategoryDepthsUsedInInsightsInExplorer !== null && (
                        <ExplorerViewNormalisedCategory
                            {...commonTabProps}
                            onFiltersChange={(value) =>
                                setAppliedFilters({
                                    ...queryFilters,
                                    normalisedCategory: value,
                                })
                            }
                            key={selectedWindow}
                            ref={normalisedCategoryRef}
                        />
                    )}

                {selectedTab === ExplorerTopLevelEntity.SUPPLIER && (
                    <ExplorerViewSupplier
                        {...commonTabProps}
                        onFiltersChange={(value) =>
                            setAppliedFilters({
                                ...queryFilters,
                                supplier: value,
                            })
                        }
                        key={selectedWindow}
                        ref={supplierRef}
                    />
                )}
            </div>
        </div>
    );
});
ExplorerView.displayName = 'ExplorerView';
